import { useEffect, useState } from "react";
import { ordersAPI } from "../../lib/api";
import { Wallet, ShoppingBag, TrendingUp, Filter, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Header } from '../../components/layout/Header';

const fmtBRL = v => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const fmtDate = iso => iso ? new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const GATEWAY_META = {
    stripe:      { label: "Stripe",       color: "text-indigo-400", bg: "bg-indigo-900/20" },
    mercadopago: { label: "Mercado Pago", color: "text-sky-400",    bg: "bg-sky-900/20" },
    hotmart:     { label: "Hotmart",      color: "text-orange-400", bg: "bg-orange-900/20" },
    manual:      { label: "Manual",       color: "text-stone-400",  bg: "bg-stone-800" },
};

const STATUS_META = {
    pendente:    { label: "Pendente",    color: "text-yellow-300", bg: "bg-yellow-900/30 border-yellow-700" },
    confirmado:  { label: "Confirmado",  color: "text-green-300",  bg: "bg-green-900/30 border-green-700" },
    reembolsado: { label: "Reembolsado", color: "text-blue-300",   bg: "bg-blue-900/30 border-blue-700" },
    cancelado:   { label: "Cancelado",   color: "text-red-300",    bg: "bg-red-900/30 border-red-700" },
};

const PRODUCT_LABELS = {
    livro_fisico:  "Livro Físico",
    livro_digital: "Livro Digital",
    mentoria:      "Mentoria",
    evento:        "Evento",
    outro:         "Outro",
};

export default function FinanceAdmin() {
    const [orders, setOrders] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const LIMIT = 25;

    const load = async () => {
        setLoading(true);
        try {
            const r = await ordersAPI.list({ skip: page * LIMIT, limit: LIMIT });
            setOrders(r.data?.orders || []);
            setTotal(r.data?.total || 0);
        } catch { toast.error("Erro ao carregar pedidos"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [page]);

    // Totais por status
    const totalConfirmado = orders.filter(o => o.status === "confirmado").reduce((s, o) => s + (o.amount || 0), 0);
    const totalPendente   = orders.filter(o => o.status === "pendente").reduce((s, o) => s + (o.amount || 0), 0);
    const countConfirmado = orders.filter(o => o.status === "confirmado").length;

    return (
        <>
        <Header title="Financeiro" subtitle="Pedidos e pagamentos" />
        <div className="p-6 space-y-6">

            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-emerald-400" />
                        <span className="text-xs text-stone-400">Receita confirmada</span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-400">{fmtBRL(totalConfirmado)}</p>
                    <p className="text-xs text-stone-500 mt-1">{countConfirmado} pedidos confirmados</p>
                </div>
                <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <ShoppingBag className="w-4 h-4 text-yellow-400" />
                        <span className="text-xs text-stone-400">Aguardando pagamento</span>
                    </div>
                    <p className="text-2xl font-bold text-yellow-400">{fmtBRL(totalPendente)}</p>
                    <p className="text-xs text-stone-500 mt-1">{orders.filter(o => o.status === "pendente").length} pedidos pendentes</p>
                </div>
                <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Wallet className="w-4 h-4 text-amber-400" />
                        <span className="text-xs text-stone-400">Total de pedidos</span>
                    </div>
                    <p className="text-2xl font-bold text-stone-100">{total}</p>
                    <p className="text-xs text-stone-500 mt-1">todos os status</p>
                </div>
            </div>

            {/* Gateways */}
            <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
                <h2 className="text-sm font-semibold text-stone-200 mb-3">Gateways de pagamento</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[
                        { key: "hotmart", link: "https://app-vlc.hotmart.com", desc: "Mentoria + e-book" },
                        { key: "mercadopago", link: "https://www.mercadopago.com.br/activities", desc: "Livro físico" },
                        { key: "stripe", link: "https://dashboard.stripe.com", desc: "Eventos (Mesa de Ester)" },
                    ].map(({ key, link, desc }) => {
                        const meta = GATEWAY_META[key];
                        const count = orders.filter(o => o.gateway === key && o.status === "confirmado").length;
                        const revenue = orders.filter(o => o.gateway === key && o.status === "confirmado").reduce((s, o) => s + (o.amount || 0), 0);
                        return (
                            <div key={key} className={`${meta.bg} border border-stone-700 rounded-xl p-4`}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className={`text-sm font-medium ${meta.color}`}>{meta.label}</span>
                                    <a href={link} target="_blank" rel="noreferrer"
                                        className="text-stone-500 hover:text-stone-300 transition-colors">
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </a>
                                </div>
                                <p className="text-xs text-stone-500">{desc}</p>
                                <div className="mt-3 space-y-1">
                                    <p className="text-lg font-bold text-stone-100">{fmtBRL(revenue)}</p>
                                    <p className="text-xs text-stone-500">{count} confirmados</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Tabela de pedidos */}
            <div className="bg-stone-900 border border-stone-800 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-stone-800">
                    <h2 className="text-sm font-semibold text-stone-200">Pedidos</h2>
                </div>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-stone-800">
                            <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Comprador</th>
                            <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Produto</th>
                            <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Gateway</th>
                            <th className="text-right px-4 py-3 text-xs text-stone-500 font-medium">Valor</th>
                            <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Status</th>
                            <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Data</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-800/50">
                        {loading ? (
                            <tr><td colSpan={6} className="text-center py-12 text-stone-500 text-xs">Carregando...</td></tr>
                        ) : orders.length === 0 ? (
                            <tr><td colSpan={6} className="text-center py-12 text-stone-500 text-xs">Nenhum pedido ainda.</td></tr>
                        ) : orders.map(o => {
                            const gwMeta  = GATEWAY_META[o.gateway]  || GATEWAY_META.manual;
                            const stMeta  = STATUS_META[o.status]    || STATUS_META.pendente;
                            return (
                                <tr key={o.id} className="hover:bg-stone-800/30 transition-colors">
                                    <td className="px-4 py-3">
                                        <p className="text-stone-200 font-medium">{o.buyer_name}</p>
                                        <p className="text-xs text-stone-500">{o.buyer_email}</p>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-stone-400">
                                        {PRODUCT_LABELS[o.product_type] || o.product_type}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`text-xs font-medium ${gwMeta.color}`}>{gwMeta.label}</span>
                                    </td>
                                    <td className="px-4 py-3 text-right font-semibold text-stone-100">
                                        {o.amount > 0 ? fmtBRL(o.amount) : "—"}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`text-xs px-2 py-0.5 rounded-full border ${stMeta.bg} ${stMeta.color}`}>
                                            {stMeta.label}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-stone-500">{fmtDate(o.paid_at || o.created_at)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {total > LIMIT && (
                    <div className="border-t border-stone-800 px-4 py-3 flex items-center justify-between">
                        <span className="text-xs text-stone-500">{page * LIMIT + 1}–{Math.min((page+1)*LIMIT, total)} de {total}</span>
                        <div className="flex gap-2">
                            <button disabled={page === 0} onClick={() => setPage(p => p-1)}
                                className="px-3 py-1 text-xs border border-stone-700 rounded-lg text-stone-400 hover:text-stone-200 disabled:opacity-30">Anterior</button>
                            <button disabled={(page+1)*LIMIT >= total} onClick={() => setPage(p => p+1)}
                                className="px-3 py-1 text-xs border border-stone-700 rounded-lg text-stone-400 hover:text-stone-200 disabled:opacity-30">Próxima</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
        </>
    );
}
